import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Sheet } from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Toolbar } from "@/components/ui/toolbar";
import { FormSection, Field } from "@/components/ui/form-section";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page-header";
import { useAuths, useCreateAuth, useUpdateAuth, useDeleteAuth } from "@/hooks/use-auths";
import type { Auth } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const defaultAuth = {
  name: "",
  auth_type: "userpass",
  username: "",
  password: "",
  password_digest: null as string | null,
};

const columns: Column<Auth>[] = [
  { key: "name", header: "Name" },
  { key: "auth_type", header: "Type", render: (a) => <span className="font-mono text-xs">{a.auth_type}</span> },
  { key: "username", header: "Username" },
];

export default function AuthsPage() {
  const { data, isLoading, error, refetch, isFetching } = useAuths();
  const createMut = useCreateAuth();
  const updateMut = useUpdateAuth();
  const deleteMut = useDeleteAuth();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Auth | null>(null);
  const [form, setForm] = useState(defaultAuth);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<Auth | null>(null);

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => [a.name, a.username].some((v) => v?.toLowerCase().includes(q)));
  }, [list, query]);

  const openCreate = () => { setEditing(null); setForm(defaultAuth); setOpen(true); };
  const openEdit = (a: Auth) => {
    setEditing(a);
    const { id, created_at, updated_at, ...rest } = a;
    setForm({ ...rest });
    setOpen(true);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, {
        onSuccess: () => { setOpen(false); toast.success("Auth updated"); },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    } else {
      createMut.mutate(form as typeof form, {
        onSuccess: () => { setOpen(false); toast.success("Auth created"); },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    }
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Auths"
        description="PJSIP authentication credentials"
      />

      <Toolbar
        search={{ value: query, onChange: setQuery, placeholder: "Search auths..." }}
        count={`${filtered.length}${query ? ` of ${list.length}` : ""}`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
        actions={<Button onClick={openCreate} size="sm"><Plus size={14} /> Create Auth</Button>}
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        getRowKey={(a) => a.id}
        onEdit={openEdit}
        onDelete={setPendingDelete}
        emptyMessage={query ? "No matches for your search." : "No auths configured."}
        error={error}
        onRetry={() => refetch()}
      />

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit Auth" : "Create Auth"}
        description="SIP credentials used to authenticate endpoints and registrations"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </>
        }
      >
        <FormSection title="Credentials" columns={2}>
          <Field label="Name" required htmlFor="name" info="Unique auth identifier. Referenced by endpoints as auth or outbound_auth. Example: trunk-auth, user-1001.">
            <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
          </Field>
          <Field label="Type" htmlFor="auth_type" info="Authentication mechanism. userpass - username/password MD5 digest auth. md5 - pre-computed MD5 digest.">
            <Select id="auth_type" value={form.auth_type} onChange={(e) => updateField("auth_type", e.target.value)}>
              <option value="userpass">User/Pass</option>
              <option value="md5">MD5</option>
            </Select>
          </Field>
          <Field label="Username" required htmlFor="username" info="SIP authentication username. For inbound auth: what Asterisk challenges callers for. For outbound auth: what Asterisk sends to the provider. Example: mytrunkuser.">
            <Input id="username" value={form.username} onChange={(e) => updateField("username", e.target.value)} />
          </Field>
          {form.auth_type === "userpass" ? (
            <Field label="Password" required htmlFor="password" info="SIP authentication password/secret. Used with username for MD5 digest calculation during SIP 401/407 challenges.">
              <Input id="password" type="password" value={form.password} onChange={(e) => updateField("password", e.target.value)} />
            </Field>
          ) : (
            <Field label="Password Digest" required htmlFor="password_digest" info='Pre-computed MD5 digest. Generate with: echo -n "myuser:somedomain:somepassword" | openssl dgst -md5'>
              <Input id="password_digest" value={form.password_digest ?? ""} onChange={(e) => updateField("password_digest", e.target.value || null)} />
            </Field>
          )}
        </FormSection>
      </Sheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Delete auth"
        description={<>Delete <strong>{pendingDelete?.name}</strong>? This cannot be undone.</>}
        loading={deleteMut.isPending}
        onConfirm={() =>
          pendingDelete &&
          deleteMut.mutate(pendingDelete.id, {
            onSuccess: () => { toast.success("Auth deleted"); setPendingDelete(null); },
            onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
          })
        }
      />
    </div>
  );
}
